import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fabricsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Search, SlidersHorizontal, Upload, X, Eye, Check, User, AlertCircle, Image } from 'lucide-react';
import { TryOnFabricPanel } from '@/components/TryOnFabricPanel';
import { VendorScoreDot } from '@/components/VendorScoreDot';
import { HelpTooltip } from '@/components/HelpTooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLOUR_MAP = {
  'navy': '#1B2A4A', 'charcoal': '#36454F', 'black': '#1a1a1a', 'white': '#F5F5F5',
  'grey': '#808080', 'gray': '#808080', 'blue': '#2563EB', 'brown': '#8B4513',
  'beige': '#D4C5A9', 'cream': '#FFFDD0', 'ivory': '#FFFFF0', 'burgundy': '#800020',
  'maroon': '#800000', 'red': '#DC2626', 'green': '#166534', 'olive': '#556B2F',
  'tan': '#D2B48C', 'khaki': '#C3B091', 'pink': '#EC4899', 'lavender': '#B4A7D6',
  'yellow': '#EAB308', 'orange': '#EA580C', 'purple': '#7C3AED', 'teal': '#0D9488',
  'wine': '#722F37', 'camel': '#C19A6B', 'stone': '#928E85', 'steel': '#71797E',
  'midnight': '#191970', 'indigo': '#4B0082', 'sage': '#B2AC88', 'rust': '#B7410E',
};

function getColourHex(colourName) {
  if (!colourName) return '#D4C5A9';
  const lower = colourName.toLowerCase();
  for (const [key, val] of Object.entries(COLOUR_MAP)) {
    if (lower.includes(key)) return val;
  }
  return '#D4C5A9';
}

const TIER_STYLES = {
  standard: 'bg-[rgba(27,42,74,0.08)] text-[#1B2A4A] border border-[rgba(27,42,74,0.15)]',
  premium: 'bg-[rgba(197,165,90,0.14)] text-[#1B2A4A] border border-[rgba(197,165,90,0.28)]',
  luxury: 'bg-[rgba(197,165,90,0.22)] text-[#1B2A4A] border border-[rgba(197,165,90,0.4)]',
};

const PATTERN_LABELS = {
  solid: 'Solid', stripe: 'Stripe', check: 'Check', herringbone: 'Herringbone',
  plaid: 'Plaid', print: 'Print', texture: 'Texture', houndstooth: 'Houndstooth',
};

export default function FabricsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === 'Admin';

  const [fabrics, setFabrics] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Try On Panel state
  const [tryOnPanelOpen, setTryOnPanelOpen] = useState(false);
  const [selectedFabricForTryOn, setSelectedFabricForTryOn] = useState(null);
  
  // Lightbox state for swatch image
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Bulk Update Images state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  
  const canTryOn = user?.role === 'Admin' || user?.role === 'Sales';

  // Filters state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'recent');
  const [filters, setFilters] = useState({
    mill_name: searchParams.get('mill_name') || '',
    colour_primary: searchParams.get('colour_primary') || '',
    pattern_type: searchParams.get('pattern_type') || '',
    occasion: searchParams.get('occasion') || '',
    season: searchParams.get('season') || '',
    price_tier: searchParams.get('price_tier') || '',
    is_available: searchParams.get('is_available') || '',
  });

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const fetchFabrics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy, page: 1, page_size: 200 };
      if (searchQuery) params.q = searchQuery;
      if (filters.mill_name) params.mill_name = filters.mill_name;
      if (filters.colour_primary) params.colour_primary = filters.colour_primary;
      if (filters.pattern_type) params.pattern_type = filters.pattern_type;
      if (filters.occasion) params.occasion = filters.occasion;
      if (filters.season) params.season = filters.season;
      if (filters.price_tier) params.price_tier = filters.price_tier;
      if (filters.is_available === 'true') params.is_available = true;
      if (filters.is_available === 'false') params.is_available = false;

      const response = await fabricsAPI.list(params);
      setFabrics(response.data.fabrics);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load fabrics');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, sortBy, filters]);

  useEffect(() => {
    fabricsAPI.getFilterOptions().then(r => setFilterOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchFabrics(), 300);
    return () => clearTimeout(timer);
  }, [fetchFabrics]);

  const clearFilters = () => {
    setFilters({ mill_name: '', colour_primary: '', pattern_type: '', occasion: '', season: '', price_tier: '', is_available: '' });
    setSearchQuery('');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
  };

  const handleBulkFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f && f.name.endsWith('.csv')) {
      setBulkFile(f);
      setBulkPreview(null);
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handleBulkPreview = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    try {
      const res = await fabricsAPI.bulkImagesPreview(bulkFile);
      setBulkPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkConfirm = async () => {
    if (!bulkPreview?.preview?.length) return;
    setBulkApplying(true);
    try {
      const res = await fabricsAPI.bulkImagesConfirm(bulkPreview.preview);
      toast.success(`Updated ${res.data.updated} fabric image(s)`);
      setBulkDialogOpen(false);
      setBulkFile(null);
      setBulkPreview(null);
      fetchFabrics();
    } catch (err) {
      toast.error('Bulk update failed');
    } finally {
      setBulkApplying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Fabrics</h1>
          <HelpTooltip text="Browse your fabric catalogue. Click a fabric to see details or use Try On to preview on a client." testId="help-fabrics" />
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {total} fabric{total !== 1 ? 's' : ''} in catalogue
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-3">
            <Button
              data-testid="bulk-update-images-button"
              variant="outline"
              onClick={() => setBulkDialogOpen(true)}
              className="h-11 rounded-xl px-5 text-sm font-medium border-border/80"
            >
              <Image className="w-4 h-4 mr-2" /> Bulk Images
            </Button>
            <Button
              data-testid="fabrics-import-button"
              variant="outline"
              onClick={() => navigate('/app/fabrics/import')}
              className="h-11 rounded-xl px-5 text-sm font-medium border-border/80"
            >
              <Upload className="w-4 h-4 mr-2" /> Import CSV
            </Button>
            <Button
              data-testid="fabrics-add-button"
              onClick={() => navigate('/app/fabrics/new')}
              className="h-11 rounded-xl px-5 text-sm font-medium hover:translate-y-[-1px] active:translate-y-0 active:scale-[0.99]"
              style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)', transition: 'transform 150ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 150ms cubic-bezier(0.2,0.8,0.2,1)' }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Fabric
            </Button>
          </div>
        )}
      </div>

      {/* Search & Sort Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input
            data-testid="fabrics-search-input"
            placeholder="Search by code, mill, colour..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-11 pl-10 rounded-lg bg-white border-border/80"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="fabrics-sort-select" className="h-11 w-full sm:w-48 rounded-lg bg-white border-border/80">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Recently Added</SelectItem>
            <SelectItem value="price_asc">Price: Low to High</SelectItem>
            <SelectItem value="price_desc">Price: High to Low</SelectItem>
            <SelectItem value="mill_asc">Mill Name</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => setFiltersOpen(true)}
          className={`h-11 rounded-lg px-4 border-border/80 ${activeFilterCount > 0 ? 'border-[color:var(--brand-gold)] bg-[rgba(197,165,90,0.06)]' : ''}`}
        >
          <SlidersHorizontal className="w-4 h-4 mr-2" />
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Button>
      </div>

      {/* Active Filters */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Active filters:</span>
          {Object.entries(filters).filter(([_, v]) => v !== '').map(([key, val]) => (
            <Badge key={key} variant="outline" className="text-xs px-2 py-1 rounded-md gap-1 cursor-pointer hover:bg-red-50"
              onClick={() => setFilters(prev => ({ ...prev, [key]: '' }))}>
              {key.replace(/_/g, ' ')}: {val}
              <X className="w-3 h-3" />
            </Badge>
          ))}
          <button onClick={clearFilters} className="text-xs underline" style={{ color: 'var(--danger)' }}>Clear all</button>
        </div>
      )}

      {/* Fabric Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="rounded-xl border border-border/80 bg-white">
              <CardContent className="p-0">
                <Skeleton className="h-36 w-full rounded-t-xl" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : fabrics.length === 0 ? (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardContent className="p-12 sm:p-16 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--brand-gold)' }} />
            </div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
              {searchQuery || activeFilterCount > 0 ? 'No fabrics match your search' : 'No fabrics yet'}
            </h2>
            <p className="text-sm max-w-sm mx-auto leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
              {searchQuery || activeFilterCount > 0 ? 'Try adjusting your filters or search query.' : 'Add your first fabric to start building the catalogue.'}
            </p>
            {isAdmin && !searchQuery && activeFilterCount === 0 && (
              <Button onClick={() => navigate('/app/fabrics/new')} className="mt-6 h-11 rounded-xl px-6"
                style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
                <Plus className="w-4 h-4 mr-2" /> Add First Fabric
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {fabrics.map((fabric) => (
            <Card
              key={fabric.id}
              data-testid="fabric-card"
              className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)] cursor-pointer hover:shadow-[0_18px_50px_rgba(16,24,40,0.12)] hover:border-[color:var(--border-strong)]"
              style={{ transition: 'box-shadow 220ms cubic-bezier(0.2,0.8,0.2,1), border-color 220ms cubic-bezier(0.2,0.8,0.2,1)' }}
              onClick={() => navigate(`/app/fabrics/${fabric.id}`)}
            >
              <CardContent className="p-0">
                {/* Swatch Image or Colour Placeholder */}
                <div className="h-36 rounded-t-xl relative overflow-hidden" 
                  style={{ backgroundColor: getColourHex(fabric.colour_primary) }}>
                  {fabric.swatch_image_url && (
                    <img
                      src={fabric.swatch_image_url.startsWith('/api') ? `${BACKEND_URL}${fabric.swatch_image_url}` : fabric.swatch_image_url}
                      alt={fabric.internal_code}
                      className="w-full h-full object-cover cursor-zoom-in"
                      loading="lazy"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxImage({
                          url: fabric.swatch_image_url.startsWith('/api') ? `${BACKEND_URL}${fabric.swatch_image_url}` : fabric.swatch_image_url,
                          label: fabric.internal_code,
                        });
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                      data-testid={`swatch-image-${fabric.id}`}
                    />
                  )}
                  {/* Availability badge */}
                  <div className="absolute top-2.5 right-2.5">
                    <Badge data-testid="fabric-availability-badge"
                      className={`text-[10px] font-medium px-2 py-0.5 rounded-md ${fabric.is_available ? 'bg-[rgba(30,122,95,0.85)] text-white border-0' : 'bg-[rgba(180,35,24,0.85)] text-white border-0'}`}>
                      {fabric.is_available ? 'Available' : 'Unavailable'}
                    </Badge>
                  </div>
                  {/* Pattern type */}
                  <div className="absolute bottom-2.5 left-2.5">
                    <Badge className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-white/90 text-[#1B2A4A] border-0">
                      {PATTERN_LABELS[fabric.pattern_type] || fabric.pattern_type}
                    </Badge>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--navy-ink)' }}>{fabric.internal_code}</p>
                      <p className="text-xs flex items-center gap-1" style={{ color: 'var(--ink-muted)' }}>
                        {fabric.mill_name}
                        <VendorScoreDot vendorName={fabric.vendor_name || fabric.mill_name} />
                      </p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] font-medium px-1.5 py-0 rounded-md flex-shrink-0 ${TIER_STYLES[fabric.price_tier] || ''}`}>
                      {fabric.price_tier}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full border border-border/60 flex-shrink-0" style={{ backgroundColor: getColourHex(fabric.colour_primary) }} />
                    <span className="text-xs capitalize" style={{ color: 'var(--ink-muted)' }}>
                      {fabric.colour_primary}{fabric.colour_secondary ? ` / ${fabric.colour_secondary}` : ''}
                    </span>
                  </div>
                  <p className="text-sm font-semibold tabular-nums" style={{ color: 'var(--brand-navy)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    {formatPrice(fabric.price_per_metre)}<span className="text-xs font-normal" style={{ color: 'var(--ink-muted)' }}>/m</span>
                  </p>
                  
                  {/* Try On Button */}
                  {canTryOn && (
                    <div className="relative group/tryon">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 h-8 gap-1.5 text-xs rounded-lg border-[var(--brand-gold)] text-[var(--brand-gold)] hover:bg-[rgba(197,165,90,0.08)]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFabricForTryOn(fabric);
                          setTryOnPanelOpen(true);
                        }}
                        data-testid={`try-on-fabric-${fabric.id}`}
                      >
                        <User className="w-3.5 h-3.5" /> Try On
                        {!fabric.swatch_image_url && (
                          <AlertCircle className="w-3 h-3 ml-1 text-amber-500" />
                        )}
                      </Button>
                      {/* Tooltip for missing swatch */}
                      {!fabric.swatch_image_url && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[9px] bg-amber-700 text-white rounded-md opacity-0 group-hover/tryon:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                          Upload swatch image for best results
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filter Sheet */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent className="sm:max-w-[380px] bg-white">
          <SheetHeader>
            <SheetTitle className="text-xl font-semibold" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
              Filter Fabrics
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-140px)] mt-4">
            <div className="space-y-5 pr-4">
              {/* Mill Name */}
              <div className="space-y-2">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Mill</Label>
                <Select value={filters.mill_name || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, mill_name: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-11 rounded-lg"><SelectValue placeholder="All mills" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All mills</SelectItem>
                    {filterOptions?.mills?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Colour */}
              <div className="space-y-2">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Colour</Label>
                <Select value={filters.colour_primary || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, colour_primary: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-11 rounded-lg"><SelectValue placeholder="All colours" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All colours</SelectItem>
                    {filterOptions?.colours?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Pattern Type */}
              <div className="space-y-2">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Pattern</Label>
                <Select value={filters.pattern_type || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, pattern_type: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-11 rounded-lg"><SelectValue placeholder="All patterns" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All patterns</SelectItem>
                    {filterOptions?.pattern_types?.map(p => <SelectItem key={p} value={p}>{PATTERN_LABELS[p] || p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Occasion */}
              <div className="space-y-2">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Occasion</Label>
                <Select value={filters.occasion || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, occasion: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-11 rounded-lg"><SelectValue placeholder="All occasions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All occasions</SelectItem>
                    {filterOptions?.occasions?.map(o => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Season */}
              <div className="space-y-2">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Season</Label>
                <Select value={filters.season || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, season: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-11 rounded-lg"><SelectValue placeholder="All seasons" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All seasons</SelectItem>
                    {filterOptions?.seasons?.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Price Tier */}
              <div className="space-y-2">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Price Tier</Label>
                <Select value={filters.price_tier || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, price_tier: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-11 rounded-lg"><SelectValue placeholder="All tiers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tiers</SelectItem>
                    {filterOptions?.price_tiers?.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Availability */}
              <div className="space-y-2">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Availability</Label>
                <Select value={filters.is_available || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, is_available: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-11 rounded-lg"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Available only</SelectItem>
                    <SelectItem value="false">Unavailable only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex gap-3">
                <Button variant="outline" onClick={clearFilters} className="flex-1 h-11 rounded-xl">Clear All</Button>
                <Button onClick={() => setFiltersOpen(false)} className="flex-1 h-11 rounded-xl" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>Apply</Button>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Try On Fabric Panel */}
      {selectedFabricForTryOn && (
        <TryOnFabricPanel
          fabric={selectedFabricForTryOn}
          isOpen={tryOnPanelOpen}
          onClose={() => {
            setTryOnPanelOpen(false);
            setSelectedFabricForTryOn(null);
          }}
          onSuccess={() => {
            setTryOnPanelOpen(false);
            setSelectedFabricForTryOn(null);
            toast.success('Try-on saved!');
          }}
        />
      )}

      {/* Swatch Lightbox */}
      <Dialog open={!!lightboxImage} onOpenChange={(open) => !open && setLightboxImage(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-0" data-testid="swatch-lightbox">
          {lightboxImage && (
            <div className="flex flex-col items-center">
              <img
                src={lightboxImage.url}
                alt={lightboxImage.label}
                className="max-h-[80vh] w-auto object-contain rounded-lg"
                data-testid="swatch-lightbox-image"
              />
              <p className="text-white/80 text-sm mt-3 font-medium">{lightboxImage.label}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Update Images Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!open) { setBulkDialogOpen(false); setBulkFile(null); setBulkPreview(null); } }}>
        <DialogContent className="max-w-lg" data-testid="bulk-update-images-dialog">
          <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--brand-navy)' }}>Bulk Update Images</h3>
          <p className="text-sm mb-4" style={{ color: 'var(--ink-muted)' }}>
            Upload a CSV with two columns: <strong>internal_code</strong> and <strong>swatch_image_url</strong>
          </p>

          {!bulkPreview ? (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-border/60 rounded-xl p-8 text-center cursor-pointer hover:border-[color:var(--brand-gold)]"
                onClick={() => document.getElementById('bulk-csv-upload').click()}
              >
                <input type="file" accept=".csv" onChange={handleBulkFileSelect} className="hidden" id="bulk-csv-upload" data-testid="bulk-csv-input" />
                {bulkFile ? (
                  <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{bulkFile.name}</p>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Click to select CSV file</p>
                )}
              </div>
              <Button onClick={handleBulkPreview} disabled={!bulkFile || bulkUploading} className="w-full h-11 rounded-xl"
                style={{ backgroundColor: 'var(--brand-navy)', color: 'white' }}
                data-testid="bulk-preview-button">
                {bulkUploading ? 'Processing...' : 'Preview Changes'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(30,122,95,0.06)' }}>
                  <p className="text-xl font-semibold" style={{ color: 'var(--success)' }}>{bulkPreview.will_update}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Will update</p>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(180,35,24,0.04)' }}>
                  <p className="text-xl font-semibold" style={{ color: 'var(--danger)' }}>{bulkPreview.not_found_count}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Not found</p>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.06)' }}>
                  <p className="text-xl font-semibold" style={{ color: '#D97706' }}>{bulkPreview.invalid_url_count}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Invalid URL</p>
                </div>
              </div>
              {bulkPreview.not_found_codes?.length > 0 && (
                <div className="p-3 rounded-lg text-xs" style={{ backgroundColor: 'rgba(180,35,24,0.04)', color: 'var(--danger)' }}>
                  <strong>Codes not found:</strong> {bulkPreview.not_found_codes.join(', ')}
                </div>
              )}
              {bulkPreview.will_update > 0 && (
                <div className="max-h-40 overflow-auto rounded-lg border p-2">
                  {bulkPreview.preview.slice(0, 10).map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-1 text-xs">
                      <span className="font-medium" style={{ color: 'var(--navy-ink)' }}>{r.internal_code}</span>
                      <Check className="w-3.5 h-3.5" style={{ color: 'var(--success)' }} />
                    </div>
                  ))}
                  {bulkPreview.preview.length > 10 && (
                    <p className="text-xs text-center pt-1" style={{ color: 'var(--ink-muted)' }}>
                      ...and {bulkPreview.preview.length - 10} more
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setBulkPreview(null); setBulkFile(null); }} className="flex-1 h-11 rounded-xl">Back</Button>
                <Button onClick={handleBulkConfirm} disabled={bulkApplying || bulkPreview.will_update === 0} className="flex-1 h-11 rounded-xl"
                  style={{ backgroundColor: 'var(--brand-navy)', color: 'white' }}
                  data-testid="bulk-confirm-button">
                  {bulkApplying ? 'Applying...' : `Update ${bulkPreview.will_update} Fabric${bulkPreview.will_update !== 1 ? 's' : ''}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
